// Platform-surface API wrappers — audit, health, inbox, staff. Bundled
// in one file because each surface is small and the SPA imports them
// from one place; one-file-per-resource would just spread the imports.

import type { ApiClient } from '../api-client.js';

// ─── Audit ────────────────────────────────────────────────────────

export type AuditSource = 'tenant' | 'staff';

export interface AuditRow {
  source: AuditSource;
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  actorId: string | null;
  actorEmail: string | null;
  action: string;
  target: string | null;
  meta: Record<string, unknown>;
  ip: string | null;
  userAgent: string | null;
  ts: string;
}

export interface ListAuditParams {
  actionPrefix?: string;
  tenantId?: string;
  source?: AuditSource;
  sinceHours?: number;
  limit?: number;
}

export const AUDIT_QUERY_KEY = ['admin', 'audit'] as const;

// ─── Health ───────────────────────────────────────────────────────

export interface HealthSummary {
  reconciliationLast7: { green: number; driftWarn: number; driftAlert: number; error: number };
  freezeCount30d: number;
  errorActionsLast24h: number;
  queues: { name: string; waiting: number; active: number; available: boolean }[];
}
export interface ProviderAvailability {
  provider: string;
  totalCalls: number;
  successfulCalls: number;
  successRate: number;
}

export const HEALTH_SUMMARY_QUERY_KEY = ['admin', 'health', 'summary'] as const;
export const HEALTH_PROVIDERS_QUERY_KEY = ['admin', 'health', 'providers'] as const;

// ─── Inbox ────────────────────────────────────────────────────────

export type InboxItemKind =
  | 'webhook_error'
  | 'r2_over_cap'
  | 'ticket_sla_breach'
  | 'subscription_divergence'
  | 'reconciliation_drift_alert'
  | 'cost_anomaly_freeze'
  | 'security_spike';

export type InboxSeverity = 'critical' | 'warning' | 'info';

export interface InboxItem {
  id: string;
  itemKind: InboxItemKind;
  severity: InboxSeverity;
  title: string;
  body: string;
  link: string;
  targetId: string;
  tenantId: string | null;
  tenantName: string | null;
  detectedAt: string;
}

export const INBOX_QUERY_KEY = ['admin', 'inbox'] as const;

// ─── Staff ────────────────────────────────────────────────────────

export interface StaffRow {
  clerkUserId: string;
  email: string;
  scopes: string[];
  invitedAt: string;
  lastActiveAt: string | null;
  invitedBy: string | null;
}

export const STAFF_QUERY_KEY = ['admin', 'staff'] as const;

// ─── API ──────────────────────────────────────────────────────────

export const platformApi = {
  async listAudit(client: ApiClient, params: ListAuditParams = {}): Promise<{ rows: AuditRow[] }> {
    const sp = new URLSearchParams();
    if (params.actionPrefix) sp.set('actionPrefix', params.actionPrefix);
    if (params.tenantId) sp.set('tenantId', params.tenantId);
    if (params.source) sp.set('source', params.source);
    if (params.sinceHours !== undefined) sp.set('sinceHours', String(params.sinceHours));
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const tail = sp.toString();
    return client.get<{ rows: AuditRow[] }>(`/audit${tail.length > 0 ? `?${tail}` : ''}`);
  },
  async healthSummary(client: ApiClient): Promise<HealthSummary> {
    return client.get<HealthSummary>('/health/summary');
  },
  async healthProviders(client: ApiClient): Promise<{ rows: ProviderAvailability[] }> {
    return client.get<{ rows: ProviderAvailability[] }>('/health/providers');
  },
  async listInbox(client: ApiClient): Promise<{ items: InboxItem[] }> {
    return client.get<{ items: InboxItem[] }>('/inbox');
  },
  async snoozeInbox(
    client: ApiClient,
    itemKind: InboxItemKind,
    targetId: string,
    hours: number,
  ): Promise<{ snoozedUntil: string }> {
    return client.post(
      `/inbox/${itemKind}/${encodeURIComponent(targetId)}/snooze`,
      { hours },
    );
  },
  async listStaff(client: ApiClient): Promise<{ rows: StaffRow[] }> {
    return client.get<{ rows: StaffRow[] }>('/staff');
  },
  async deleteStaff(client: ApiClient, clerkUserId: string): Promise<{ ok: true }> {
    return client.del<{ ok: true }>(`/staff/${encodeURIComponent(clerkUserId)}`);
  },
};

// ─── UI helpers ───────────────────────────────────────────────────

export function severityBadgeVariant(
  s: InboxSeverity,
): 'destructive' | 'secondary' | 'outline' {
  switch (s) {
    case 'critical':
      return 'destructive';
    case 'warning':
      return 'secondary';
    case 'info':
      return 'outline';
  }
}

export function sourceBadgeVariant(s: AuditSource): 'default' | 'secondary' {
  return s === 'staff' ? 'default' : 'secondary';
}
